import * as mutations from "../../graphql/mutations";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuestionModal from "../../components/modals/QuestionModal";
import ImageModal from "../../components/modals/ImageModal";
import DeletionModal from "../../components/modals/DeletionModal";
import LoadingModal from "../../components/modals/LoadingModal";
import NewModuleModal from "../../components/modals/NewModuleModal";
import NotificationSnackbar from "../../components/NotificationSnackbar";
import StandardQuestion from "../assessmentComponents/StandardQuestions";
import StructuredEntries from "../assessmentComponents/StructuredEntries";
import StructuredEntryModal from "../../components/modals/StructuredEntryModal";
import { apiClient } from "../../helpers/apiClient";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchAssessmentById,
  fetchModulesByAssessment,
  fetchRequirementsByQuestion,
  fetchStructuredEntriesByModule,
  fetchStructuredEntryById,
  fetchQuestionsByModule,
  fetchRecommendationsByRequirement,
} from "../../helpers/apiRequests";
import {
  CGI_BLACK,
  CGI_GRADIENT_A,
  CGI_GREY,
  languageKey,
  CGI_PURPLE,
  versions,
  CGI_GRADIENT_A_45DEG,
} from "../../helpers/enums";
import {
  getEnumKeyByValue,
  sortDataBySortingNumber,
} from "../../helpers/helpers";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";

export default function EditAssessment({
  language,
  backRoute = "/manageAssessments",
  backLabelKey = "backToAssessments",
}) {
  const { t } = useTranslation();
  const { selectedAssessmentId } = useParams();

  const navigate = useNavigate();

  const emptyAssessment = {
    name: "",
    description: "",
    type: getEnumKeyByValue(versions, versions.STANDARD),
    active: false,
    public: false,
    modules: [],
    imageKey: "",
  };

  const emptyModule = {
    id: "",
    sortingNumber: 0,
    title: "",
  };

  const emptyQuestion = {
    id: "",
    title: "",
    package: "",
    text: "",
    source: "",
    requirements: [],
  };

  const emptyStructuredEntry = {
    id: "",
    title: "",
    description: "",
    source: "",
    entries: [],
  };

  const emptyIdNameAndType = {
    id: "",
    name: "",
    type: "",
  };

  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [openLoading, setOpenLoading] = useState(true);
  const [openDeleteModule, setOpenDeleteModule] = useState(false);
  const [openDeleteQuestion, setOpenDeleteQuestion] = useState(false);
  const [openDeleteStructuredEntry, setOpenDeleteStructuredEntry] =
    useState(false);
  const [toDeleteIdNameAndType, setToDeleteIdNameAndType] =
    useState(emptyIdNameAndType);
  const [openEditModule, setOpenEditModule] = useState(false);
  const [openEditQuestion, setOpenEditQuestion] = useState(false);
  const [openEditStructuredEntry, setOpenEditStructuredEntry] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState(emptyAssessment);
  const [selectedModuleId, setSelectedModuleId] = useState("");
  const [selectedModule, setSelectedModule] = useState(emptyModule);
  const [selectedQuestion, setSelectedQuestion] = useState(emptyQuestion);
  const [selectedStructuredEntry, setSelectedStructuredEntry] =
    useState(emptyStructuredEntry);

  const handleEditQuestion = (event, question) => {
    event.stopPropagation();
    setSelectedQuestion(question);
  };

  const handleEditStructuredEntry = (event, structuredEntry) => {
    event.stopPropagation();
    setSelectedStructuredEntry(structuredEntry);
  };

  async function deleteQuestion(toDeleteId) {
    try {
      const requirementsByQuestion =
        await fetchRequirementsByQuestion(toDeleteId);

      for (const requirement of requirementsByQuestion) {
        await apiClient.graphql({
          query: mutations.deleteRequirement,
          variables: { input: { id: requirement.id } },
        });
      }

      await apiClient.graphql({
        query: mutations.deleteQuestion,
        variables: { input: { id: toDeleteId } },
      });

      fetchAssessmentData();
      setNotificationMessage(t("itemDeleted!"));
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenDeleteQuestion(false);
    setOpenNotification(true);
  }

  async function deleteStructuredEntry(toDeleteId) {
    try {
      await apiClient.graphql({
        query: mutations.deleteStructuredEntry,
        variables: { input: { id: toDeleteId } },
      });
      fetchAssessmentData();
      setNotificationMessage(t("itemDeleted!"));
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenDeleteQuestion(false);
    setOpenNotification(true);
  }

  async function deleteModule(toDeleteId) {
    try {
      const questionsByModule = await fetchQuestionsByModule(toDeleteId);

      await Promise.all(
        questionsByModule.map(async (question) => {
          const requirementsByQuestion = await fetchRequirementsByQuestion(
            question.id,
          );
          for (const requirement of requirementsByQuestion) {
            await apiClient.graphql({
              query: mutations.deleteRequirement,
              variables: { input: { id: requirement.id } },
            });
          }
        }),
      );

      for (const question of questionsByModule) {
        await apiClient.graphql({
          query: mutations.deleteQuestion,
          variables: { input: { id: question.id } },
        });
      }

      const structuredEntries =
        await fetchStructuredEntriesByModule(toDeleteId);
      await Promise.all(
        structuredEntries.map(async (entry) => {
          await apiClient.graphql({
            query: mutations.deleteStructuredEntry,
            variables: { input: { id: entry.id } },
          });
        }),
      );

      await apiClient.graphql({
        query: mutations.deleteModule,
        variables: { input: { id: toDeleteId } },
      });

      fetchAssessmentData();
      setNotificationMessage(t("itemDeleted!"));
    } catch (error) {
      console.error(error);
      setNotificationMessage(t("errorMessage"));
    }
    setOpenNotification(true);
  }

  async function fetchAssessmentData() {
    setOpenLoading(true);
    try {
      const assessmentData = await fetchAssessmentById(selectedAssessmentId);
      const assessment = {
        name: assessmentData.name,
        contactPerson: assessmentData.contactPerson,
        language: assessmentData.language,
        type: assessmentData.type,
        description: assessmentData.description,
        imageKey: assessmentData.imageKey,
        modules: [],
      };
      const moduleData = await fetchModulesByAssessment(selectedAssessmentId);
      const modules = await Promise.all(
        moduleData.map(async (m) => {
          const questionData = await fetchQuestionsByModule(m.id);
          const questions = await Promise.all(
            questionData.map(async (question) => {
              const requirementData = await fetchRequirementsByQuestion(
                question.id,
              );
              const requirements = await Promise.all(
                requirementData.map(async (r) => {
                  const recommendationsResult =
                    await fetchRecommendationsByRequirement(r.id);
                  const recommendations = (
                    recommendationsResult.items || []
                  ).map((rec) => ({
                    id: rec.id,
                    level: rec.level,
                    description: rec.description,
                    recommendationText: rec.recommendationText,
                  }));

                  return {
                    ...r,
                    recommendations,
                  };
                }),
              );

              return {
                ...question,
                requirements,
              };
            }),
          );
          const structuredEntryData = await fetchStructuredEntriesByModule(
            m.id,
          );
          const structuredEntries = await Promise.all(
            structuredEntryData.map(async (entry) => {
              const structuredEntry = await fetchStructuredEntryById(entry.id);
              const entries = structuredEntry.entries.map((e) => ({
                impact: e.impact,
                risk: e.risk,
                opportunity: e.opportunity,
                example: e.example,
              }));
              return {
                ...structuredEntry,
                entries,
              };
            }),
          );
          return {
            ...m,
            questions,
            structuredEntries,
          };
        }),
      );
      assessment.modules = modules;
      setSelectedAssessment(assessment);
    } catch (error) {
      console.error(error);
      navigate(backRoute);
    }
    setOpenLoading(false);
  }

  useEffect(() => {
    fetchAssessmentData();
  }, []);

  useEffect(() => {
    if (selectedModule.id !== "") {
      setOpenEditModule(true);
    }
  }, [selectedModule]);

  useEffect(() => {
    if (selectedQuestion.id !== "") {
      setOpenEditQuestion(true);
    }
  }, [selectedQuestion]);

  useEffect(() => {
    if (selectedQuestion.id !== "") {
      setOpenEditStructuredEntry(true);
    }
  }, [selectedStructuredEntry]);

  useEffect(() => {
    if (toDeleteIdNameAndType.id !== "") {
      if (toDeleteIdNameAndType.type === "Module") {
        setOpenDeleteModule(true);
      } else if (toDeleteIdNameAndType.type === "Question") {
        setOpenDeleteQuestion(true);
      } else {
        setOpenDeleteStructuredEntry(true);
      }
    }
  }, [toDeleteIdNameAndType]);

  return (
    <>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(backRoute)}
        sx={{
          borderBottom: 1,
          borderRight: 1,
          borderRadius: 0,
          borderBottomRightRadius: 8,
          pr: 1.2,
          py: 0.3,
        }}
      >
        {t(backLabelKey)}
      </Button>
      <Container maxWidth="xl">
        <Box sx={{ width: 1, display: "flex", flexDirection: "column", mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: 18, ml: 2 }}>
                {`${t("manageAssessment")}: ${
                  selectedAssessment.name || selectedAssessmentId
                }`}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ my: 1 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              transform: "scale(0.975)",
              borderRadius: 10,
              border: "double 2px transparent",
              backgroundImage:
                "linear-gradient(white, white)," + CGI_GRADIENT_A,
              backgroundOrigin: "border-box",
              backgroundClip: "content-box, border-box",
              my: 1.5,
              width: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                py: 2,
                px: 3,
                width: 1,
              }}
              key={selectedAssessmentId}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <Typography
                    sx={{
                      background: CGI_GRADIENT_A_45DEG,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      fontWeight: 500,
                      fontStyle: "italic",
                      fontSize: 19.5,
                      pr: 2,
                      userSelect: "none",
                    }}
                  >
                    {selectedAssessment.name}
                  </Typography>
                </Box>
                {selectedAssessment?.contactPerson && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        pr: 1,
                        fontStyle: "italic",
                        userSelect: "none",
                        color: CGI_PURPLE,
                      }}
                    >
                      {t("contactPerson")}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 15,
                        fontWeight: 500,
                        userSelect: "none",
                        color: CGI_PURPLE,
                      }}
                    >
                      {selectedAssessment.contactPerson}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Divider
                sx={{
                  my: 1,
                  borderImageSlice: 1,
                  borderImageSource: CGI_GRADIENT_A,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography
                    align="justify"
                    sx={{
                      mr: 3,
                      userSelect: "none",
                      fontFamily: "Inter",
                      fontSize: 13.5,
                    }}
                  >
                    {selectedAssessment.description}
                  </Typography>
                  <Typography
                    align="justify"
                    sx={{
                      mr: 3,
                      ml: 1,
                      fontStyle: "italic",
                      mt: 1,
                      fontSize: 13.5,
                      userSelect: "none",
                      color: CGI_PURPLE,
                    }}
                  >
                    {`${t("language")}: `}
                    {t(languageKey[selectedAssessment.language], {
                      lng: language,
                    })}
                  </Typography>
                </Box>
                <ImageModal
                  imageKey={selectedAssessment?.imageKey}
                  boxWidth={230}
                  boxHeight={230}
                />
              </Box>
            </Box>
          </Box>
          {selectedAssessment.modules.length === 0 && (
            <Typography
              sx={{ alignSelf: "center", mt: 5, mb: 2, fontSize: 17 }}
            >
              {t("noModules")}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ alignSelf: "center", mb: 2 }}
            onClick={() => {
              setSelectedModule(emptyModule);
              setOpenEditModule(true);
            }}
          >
            {t("createModule")}
          </Button>
          {selectedAssessment.modules
            ?.sort(sortDataBySortingNumber)
            .map((mData, mIndex) => (
              <Accordion
                key={mIndex}
                sx={{
                  mx: 2,
                  border: 1,
                  borderRadius: "20px !important",
                  borderColor: CGI_GREY,
                  mb: 1,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 16 }}>
                      Module {mIndex + 1}: {mData.title}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        mr: 3,
                        borderRight: 1,
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 16, pr: 1, fontStyle: "italic", mr: 5 }}
                      >
                        ({t("sortingNumber")}: {mData.sortingNumber})
                      </Typography>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedModule(mData);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        sx={{ mx: 1 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setToDeleteIdNameAndType({
                            id: mData.id,
                            name: mData.title,
                            type: "Module",
                          });
                          setOpenDeleteModule(true);
                        }}
                      >
                        <DeleteForeverIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ borderTop: 1, borderColor: "#bbb" }}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: 16, ml: 2, fontStyle: "italic", pr: 1 }}
                      >
                        {t("manageQuestions")}{" "}
                      </Typography>
                      <Button
                        onClick={() => {
                          setSelectedModuleId(mData.id);
                          if (
                            versions[selectedAssessment.type] ===
                            versions.STANDARD
                          ) {
                            setSelectedQuestion(emptyQuestion);
                            setOpenEditQuestion(true);
                          } else {
                            setSelectedQuestion(emptyStructuredEntry);
                            setOpenEditStructuredEntry(true);
                          }
                        }}
                        variant="outlined"
                        startIcon={<AddIcon />}
                        sx={{
                          borderRadius: 10,
                          py: 0,
                          textTransform: "none",
                          color: CGI_BLACK,
                        }}
                      >
                        {t("createQuestion")}
                      </Button>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    {mData.length === 0 && (
                      <Typography
                        sx={{ alignSelf: "center", mt: 2, fontSize: 16, mb: 1 }}
                      >
                        {t("noQuestions")}
                      </Typography>
                    )}
                    {versions[selectedAssessment.type] === versions.STANDARD ? (
                      <StandardQuestion
                        mData={mData}
                        handleEditQuestion={handleEditQuestion}
                        setSelectedModule={setSelectedModule}
                        setToDeleteIdNameAndType={setToDeleteIdNameAndType}
                      />
                    ) : (
                      <StructuredEntries
                        moduleData={mData}
                        handleEditStructuredEntry={handleEditStructuredEntry}
                        setSelectedModule={setSelectedModule}
                        setToDeleteIdNameAndType={setToDeleteIdNameAndType}
                      />
                    )}
                  </Box>
                </AccordionDetails>
              </Accordion>
            ))}
        </Box>
      </Container>
      <LoadingModal open={openLoading} />
      <NotificationSnackbar
        message={notificationMessage}
        open={openNotification}
        setOpen={setOpenNotification}
      />
      <DeletionModal
        open={openDeleteModule}
        setOpen={setOpenDeleteModule}
        deleteFunction={deleteModule}
        safeDelete={true}
        itemClass={t("module")}
        name={toDeleteIdNameAndType.name}
        id={toDeleteIdNameAndType.id}
      />
      <DeletionModal
        open={openDeleteQuestion}
        setOpen={setOpenDeleteQuestion}
        deleteFunction={deleteQuestion}
        safeDelete={true}
        itemClass={t("question")}
        name={toDeleteIdNameAndType.name}
        id={toDeleteIdNameAndType.id}
      />
      <DeletionModal
        open={openDeleteStructuredEntry}
        setOpen={setOpenDeleteStructuredEntry}
        deleteFunction={deleteStructuredEntry}
        safeDelete={true}
        itemClass={t("question")}
        name={toDeleteIdNameAndType.name}
        id={toDeleteIdNameAndType.id}
      />
      <NewModuleModal
        dataLabel={t("module")}
        open={openEditModule}
        setOpen={setOpenEditModule}
        setNotificationMessage={setNotificationMessage}
        setOpenNotification={setOpenNotification}
        module={selectedModule}
        assessmentId={selectedAssessmentId}
        version={selectedAssessment.type}
        fetchAssessmentData={fetchAssessmentData}
      />
      <QuestionModal
        open={openEditQuestion}
        setOpen={setOpenEditQuestion}
        assessmentId={selectedAssessmentId}
        moduleId={selectedModuleId}
        question={selectedQuestion}
        fetchAssessmentData={fetchAssessmentData}
        setOpenLoading={setOpenLoading}
        setNotificationMessage={setNotificationMessage}
        setOpenNotification={setOpenNotification}
      />
      <StructuredEntryModal
        open={openEditStructuredEntry}
        setOpen={setOpenEditStructuredEntry}
        moduleId={selectedModuleId}
        structuredEntry={selectedStructuredEntry}
        fetchAssessmentData={fetchAssessmentData}
        setOpenLoading={setOpenLoading}
        setNotificationMessage={setNotificationMessage}
        setOpenNotification={setOpenNotification}
      />
    </>
  );
}
